import { Modal, useModals } from 'almanac-ui/Modal'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { FeaturedMedia } from 'types'
import { IntroStep } from './steps/IntroStep'
import { MediaSelectionStep } from './steps/MediaSelectionStep'
import { VisualStyleSelectionStep } from './steps/VisualStyleSelectionStep'
import { FontPairingSelectionStep } from './steps/FontPairingSelectionStep'
import { AnimatedImagesOptInStep } from './steps/AnimatedImagesOptInStep'
import { ContentPlanStep } from './steps/ContentPlanStep'
import { RegenerateConfirmStep } from './steps/RegenerateConfirmStep'
import { MobileVisualStylesFlow } from './MobileVisualStylesFlow'
import { useVisualStylesFlow, StepConfig } from './useVisualStylesFlow'
import Styles from './VisualStylesForExistingUsersModal.module.scss'

// Type for styled image data passed to child component
export interface StyledImageData {
  id: number
  name: string
  styledUrl: string | null
  isLoading: boolean
  isRecommended: boolean
}

// Type for content plan values
export interface ContentPlanValues {
  socialPostsCount: number
  shortFormVideosCount: number
  blogPostsCount: number
  emailsCount: number
  featuredMedia: FeaturedMedia[]
  mediaWeights: {
    video: number
    image: number
    text: number
  }
}

const DESKTOP_STEP_CONFIG: StepConfig = {
  sequence: [
    'intro',
    'media-selection',
    'photo-style',
    'font-pairing',
    'animated-images',
    'content-plan',
    'regenerate',
  ],
  triggerGenerationOnStep: 'media-selection',
}

export const VisualStylesForExistingUsersModal = () => {
  const { closeModal } = useModals()
  const { isMobile } = useResponsiveDesign()

  if (isMobile) {
    return <MobileVisualStylesFlow onClose={closeModal} />
  }

  return <DesktopVisualStylesModal />
}

const DesktopVisualStylesModal = () => {
  const { closeModal } = useModals()

  const flow = useVisualStylesFlow({
    stepConfig: DESKTOP_STEP_CONFIG,
    onClose: closeModal,
  })

  const renderStep = () => {
    switch (flow.currentStep) {
      case 'intro':
        return <IntroStep onNext={flow.handleNext} />
      case 'media-selection':
        return (
          <MediaSelectionStep
            selectedMediaFileId={flow.selectedMediaFileId}
            onSelectMediaFile={flow.setSelectedMediaFileId}
            onNext={flow.handleNext}
            onBack={flow.handleBack}
            onClose={closeModal}
            brandKitId={flow.brandKitId as number}
          />
        )
      case 'photo-style':
        return (
          <VisualStyleSelectionStep
            selectedStyleId={flow.selectedVisualStyleId}
            onSelectStyle={flow.setSelectedVisualStyleId}
            onNext={flow.handleNext}
            onBack={flow.handleBack}
            onClose={closeModal}
            sourceImage={flow.selectedSourceImage}
            styledImages={flow.styledImages}
            onBrowseStyleSelect={flow.handleBrowseStyleSelect}
          />
        )
      case 'font-pairing':
        return (
          <FontPairingSelectionStep
            selectedFontPairingId={flow.selectedFontPairingId}
            onSelectFontPairing={async (id) => {
              await flow.deleteAllCustomFonts()
              flow.setSelectedFontPairingId(id)
            }}
            onNext={flow.handleNext}
            onBack={flow.handleBack}
            onClose={closeModal}
            isLoading={flow.recommendations.isLoading}
            recommendedIds={flow.recommendations.fontPairingIds}
            selectedStyle={flow.selectedStyleData}
            brandColor={flow.brandColor}
          />
        )
      case 'animated-images':
        return (
          <AnimatedImagesOptInStep
            onOptIn={(optIn) => {
              flow.setAnimatedImagesOptIn(optIn)
              flow.handleNext()
            }}
            onBack={flow.handleBack}
            onClose={closeModal}
          />
        )
      case 'content-plan':
        return (
          <ContentPlanStep
            animatedImagesOptIn={flow.animatedImagesOptIn}
            onContentPlanChange={flow.setContentPlanValues}
            onNext={flow.handleNext}
            onBack={flow.handleBack}
            onClose={closeModal}
          />
        )
      case 'regenerate':
        return (
          <RegenerateConfirmStep
            isSaving={flow.isSaving || flow.isRegenerating}
            onRegenerate={flow.handleRegenerate}
            onSkip={flow.handleSkipRegenerate}
          />
        )
    }
  }

  const getModalSize = () => {
    if (flow.currentStep === 'regenerate') {
      return 'sm'
    }
    if (flow.currentStep === 'intro') {
      return 'md'
    }
    return 'fullscreen'
  }

  return (
    <div
      data-autopilot-system-redesign={flow.currentStep === 'intro' || flow.currentStep === 'regenerate' ? 'off' : 'on'}
    >
      <Modal.Root
        size={getModalSize()}
        onPressOutside={() => {}}
        bodyClassName={getModalSize() === 'fullscreen' ? Styles.fullHeightModal : undefined}
      >
        {renderStep()}
      </Modal.Root>
    </div>
  )
}
